'use client';

import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import useIsMobile from '@/hooks/use-is-mobile';
import { ADD_STYLE_IN_FALL_AND_WINTER } from '@/typed-graphql/mutation';
import { gql, useMutation } from '@apollo/client';
import clsx from 'clsx';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import HoverPrefetchLink from './hover-prefetch-link';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';

const HIDE_RECOMMENDATION_PATHNAMES = [
  '/cart',
  '/checkout',
  '/checkout/onepage/success',
  '/checkout/retry',
  '/checkout/pay-result',
  '/checkout/pay-cancel',
];

const DressStyleRecommendation = () => {
  const [open, setOpen] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [reqAddStyleInFallAndWinter] = useMutation(gql(ADD_STYLE_IN_FALL_AND_WINTER));
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();

  const [currentType, setCurrentType] = useState('');
  const isSpring = websiteEnv.isAustralia || websiteEnv.isNewZealand;
  const dataSource =
    currentType === 'color'
      ? isSpring
        ? springColorConfigs
        : fallColorConfigs
      : isSpring
        ? springStyleConfigs
        : fallStyleConfigs;
  const dataSourceTitle =
    currentType === 'style'
      ? isSpring
        ? 'S/S HOT STYLES'
        : 'F/W HOT STYLES'
      : isSpring
        ? websiteEnv.isUnitedKingdom
          ? 'S/S HOT COLOURS'
          : 'S/S HOT COLORS'
        : websiteEnv.isUnitedKingdom
          ? 'F/W HOT COLOURS'
          : 'F/W HOT COLORS';

  const handleLinkClick = field => {
    if (!field) return;

    reqAddStyleInFallAndWinter({
      variables: {
        input: {
          [field]: 1,
        },
      },
    });
  };

  useEffect(() => {
    setShowRecommendation(false);

    if (HIDE_RECOMMENDATION_PATHNAMES.includes(pathname)) return;

    const storageShowRecommendation = getLocalDressRecommendation() !== '0';

    if (!storageShowRecommendation) return;

    const { prevUrl, ignored, prevPageIgnored } = getSessionDressRecommendation();

    if (ignored >= 3 || (prevPageIgnored && ignored >= 2)) {
      setLocalDressRecommendation('0');
      removeSessionDressRecommendation();
      return;
    }

    const queryStr = searchParams.toString();
    const nextUrl = pathname + (queryStr ? `?${queryStr}` : '');

    setShowRecommendation(true);
    setSessionDressRecommendation({
      prevUrl: nextUrl,
      ignored: prevUrl === nextUrl ? ignored : ignored + (prevPageIgnored ? 1 : 0),
      prevPageIgnored: 1,
    });
  }, [pathname, searchParams]);

  const handleSetOpen = nextOpen => {
    const nextState = typeof nextOpen === 'function' ? nextOpen(open) : nextOpen;

    if (nextState) {
      const prevInfo = getSessionDressRecommendation();
      setSessionDressRecommendation({ ...prevInfo, ignored: 0, prevPageIgnored: 0 });
    }

    setOpen(nextOpen);
  };

  if (HIDE_RECOMMENDATION_PATHNAMES.includes(pathname) || !showRecommendation) {
    return null;
  }

  return (
    <>
      <div
        className={clsx(
          ' fixed bottom-[22.2%] left-0 z-[200] items-center  lg_bottom-0 lg_left-1/2  lg_-translate-x-1/2 ',
          'flex',
        )}
      >
        <div
          className={clsx(
            ' h-[315px] w-[50px] flex-none lg_h-[83px] lg_w-[487px]',
            'cursor-pointer rounded-[2px] lg_rounded-[4px]',
            'flex flex-col items-center gap-y-2.5 bg-white py-2.5 lg_flex-row lg_justify-between lg_px-5 lg_py-[14px]',
            'shadow-[0_-8px_20px_0_rgba(0,0,0,0.1)] ',
          )}
          onClick={() => {
            handleSetOpen(x => !x);
          }}
        >
          <div className='relative w-full flex-1 lg_flex-auto'>
            <div className='absolute left-1/2 top-1/2 w-max -translate-x-1/2 -translate-y-1/2 -rotate-90 lg_relative lg_left-auto lg_top-auto lg_w-auto lg_translate-x-0 lg_translate-y-0 lg_rotate-0'>
              <div className='text-[22px] leading-none lg_text-[24px] lg_font-semibold lg_leading-[1.4]'>
                {isSpring ? 'WHAT TO WEAR IN S/S?' : 'WHAT TO WEAR IN F/W?'}
              </div>
              <div className='text-[13px] leading-[1.4] lg_text-[15px] lg_uppercase'>Tap for dress recommendation</div>
            </div>
          </div>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='48'
            height='47'
            fill='none'
            viewBox='0 0 48 47'
            className='h-auto w-7 flex-none lg_w-[47px]'
          >
            <path
              fill='#403732'
              d='M36.118 26.877h-.259c-.619 0-1.114.294-1.728.614v-.041c0-1.577-1.255-3.03-2.868-3.03h-.258c-.62 0-1.172.356-1.786.677v-.116c0-1.576-1.197-3.017-2.81-3.017h-.259c-.62 0-1.229.343-1.843.664V15.48c0-1.576-1.14-2.726-2.753-2.726h-.258c-1.613 0-3.13 1.15-3.13 2.726v18.41l-2.65-2.674c-.553-.535-1.235-.83-2.012-.83a2.88 2.88 0 0 0-2.036.83l-.172.177a2.8 2.8 0 0 0-.857 2.028c0 .767.31 1.487.841 2l7.109 7.803a2.46 2.46 0 0 0 1.863.845H36.58c1.152 0 2.12-.786 2.374-1.84l.09-.004V29.844c0-1.576-1.313-2.967-2.926-2.967m1.672 14.806c0 .639-.543 1.158-1.21 1.158H20.252a1.24 1.24 0 0 1-.944-.433l-7.143-7.84a1.59 1.59 0 0 1-.001-2.293l.184-.178a1.72 1.72 0 0 1 1.207-.483c.456 0 .783.171 1.105.483l4.735 4.696V15.48c0-.899.965-1.498 1.9-1.498h.26c.935 0 1.524.6 1.524 1.498v9.156l1.307.018c.125-.788.933-1.462 1.764-1.462h.259c.936 0 1.582.89 1.582 1.789v2.124l1.278.012c.125-.79.904-1.469 1.736-1.469h.258c.936 0 1.64.903 1.64 1.802v2.05l1.25.042c.125-.788.875-1.437 1.706-1.437h.259c.936 0 1.698.785 1.698 1.685zM13.255 13.368H8.957v1.228h4.298zM21.85 2.93h-1.228v4.298h1.228zM16.866 9.18l-3.197-3.104-.856.881 3.197 3.104zm12.967 5.415h4.298v-1.228h-4.298zm.185-7.637-.856-.882-3.196 3.104.855.881z'
            ></path>
          </svg>
        </div>
      </div>

      <Drawer
        open={open}
        onOpenChange={nextOpen => {
          handleSetOpen(nextOpen);
        }}
        direction={isMobile ? 'left' : 'bottom'}
        handleOnly
      >
        <DrawerContent
          className={clsx(
            'fixed left-0 top-0 z-[600] block h-screen w-full max-w-[310px] gap-y-0 border-[0]    bg-white  px-0 py-5 outline-0 supports-[height:100dvh]_h-[100dvh] lg_bottom-0 lg_top-auto lg_h-auto lg_max-w-none lg_bg-transparent lg_p-0 ',
            'lg_flex lg_justify-center',
          )}
          onClick={() => {
            if (!isMobile) {
              handleSetOpen(false);
            }
          }}
          style={{ willChange: 'auto' }}
        >
          <DrawerTitle className='hidden'>WHAT TO WEAR IN F/W?</DrawerTitle>
          <div
            onClick={() => {
              if (!isMobile) {
                handleSetOpen(false);
              }
            }}
            className={clsx('h-full bg-white lg_h-auto lg_w-full lg_max-w-[1088px] lg_bg-transparent')}
          >
            <div
              onClick={e => {
                e.stopPropagation();
              }}
              className={clsx(
                'mx-auto hidden w-full max-w-[487px] rounded-tl-[8px] rounded-tr-[8px] bg-white py-6 text-center text-[23px] font-medium leading-[1.4] lg_block',
                'relative after_absolute after_bottom-[-5px] after_left-0 after_z-[1] after_h-2.5 after_w-full after_bg-white after_content-[""]',
                'before_absolute before_bottom-0 before_left-1/2 before_z-[2] before_h-[1px] before_w-[58.9%] before_-translate-x-1/2 before_bg-E7DED5 before_content-[""]',
              )}
            >
              DRESS RECOMMENDATION
            </div>

            <div
              className={clsx(
                'flex h-full flex-col bg-white lg_block lg_h-auto',
                currentType
                  ? 'lg_px-5 lg_pb-[30px] lg_pt-[14px]'
                  : 'items-center justify-center lg_px-5 lg_pb-[27px] lg_pt-[14px]',
                'relative',
              )}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {currentType ? null : (
                <button
                  type='button'
                  className='absolute right-3 top-0 underline lg_hidden'
                  onClick={() => {
                    setLocalDressRecommendation('0');
                    removeSessionDressRecommendation();
                    setShowRecommendation(false);
                    setOpen(false);
                  }}
                >
                  Dismiss
                </button>
              )}
              <div className='hidden p-1 lg_flex lg_items-center lg_justify-between'>
                <button
                  type='button'
                  className={clsx(!currentType && 'invisible', 'text-[18px] leading-[1.4] underline')}
                  onClick={() => {
                    setCurrentType('');
                  }}
                >
                  Back
                </button>
                <button
                  type='button'
                  onClick={() => {
                    handleSetOpen(false);
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='19'
                    fill='none'
                    viewBox='0 0 18 19'
                  >
                    <path
                      stroke='#80583F'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='M17 17.5 1 1.5m16 0-16 16'
                    ></path>
                  </svg>
                </button>
              </div>
              {currentType ? null : (
                <div className='lg_py-[92px]'>
                  <div className='mx-auto max-w-[240px] font-Shango text-[22px] lg_max-w-none lg_text-center lg_text-[30px] lg_leading-[1.2]'>
                    I WANT SOME SELECTION TIPS
                  </div>
                  <div className='mt-[30px] flex flex-col flex-wrap gap-y-5 lg_mt-10 lg_flex-row lg_items-center lg_justify-center lg_gap-x-10'>
                    <button
                      className={clsx(
                        'flex h-12 min-w-[240px] items-center justify-center rounded-[3px] px-1 text-center text-[15px] font-medium leading-[1.4]  text-white lg_min-w-auto lg_px-[68px]',
                        isSpring ? 'bg-[#33662B]' : 'bg-[#B64A26]',
                      )}
                      type='button'
                      onClick={() => {
                        setCurrentType('style');
                      }}
                    >
                      {isSpring ? 'BASED ON S/S HOT STYLES' : 'BASED ON F/W HOT STYLES'}
                    </button>
                    <button
                      className={clsx(
                        'flex h-12 min-w-[240px] items-center justify-center rounded-[3px] px-1 text-center  text-[15px] font-medium leading-[1.4] text-white lg_min-w-auto lg_px-[68px]',
                        isSpring ? 'bg-[#33662B]' : 'bg-[#B64A26]',
                      )}
                      type='button'
                      onClick={() => {
                        setCurrentType('color');
                      }}
                    >
                      {isSpring
                        ? 'BASED ON S/S HOT COLORS'
                        : websiteEnv.isUnitedKingdom
                          ? 'BASED ON F/W HOT COLOURS'
                          : 'BASED ON F/W HOT COLORS'}
                    </button>
                  </div>
                </div>
              )}

              {currentType ? (
                <>
                  <div className='flex flex-none items-center justify-between px-[18px] lg_hidden'>
                    <span className='font-Shango text-[22px] leading-none'>{dataSourceTitle}</span>
                    <button
                      type='button'
                      onClick={() => {
                        handleSetOpen(false);
                      }}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='19'
                        fill='none'
                        viewBox='0 0 18 19'
                      >
                        <path
                          stroke='#80583F'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='1.5'
                          d='M17 17.5 1 1.5m16 0-16 16'
                        ></path>
                      </svg>
                    </button>
                  </div>
                  <div className='hidden text-center font-Shango text-[30px] leading-[1.2] lg_block'>
                    {dataSourceTitle}
                  </div>
                  <div className='flex-1 overflow-hidden'>
                    <div className='h-full overflow-auto lg_h-auto'>
                      <ul className='grid  grid-cols-2 gap-x-[14px] gap-y-10 px-[18px] pt-[30px] lg_mt-5 lg_flex lg_flex-wrap lg_items-start lg_justify-center lg_gap-x-5 lg_px-0 lg_pt-0'>
                        {dataSource.map(({ label, imageUrl, mobileImageUrl, href, field }) => {
                          return (
                            <li
                              key={label}
                              className='flex-none'
                            >
                              <HoverPrefetchLink
                                className='block lg_hover_shadow-[0_0_25px_0_rgba(0,0,0,0.1)]'
                                href={href}
                                onClick={() => {
                                  handleSetOpen(false);
                                  handleLinkClick(field);
                                }}
                              >
                                <Image
                                  alt={label}
                                  width={130}
                                  height={180}
                                  src={mobileImageUrl}
                                  className='block h-auto w-full lg_hidden'
                                  unoptimized
                                />
                                <Image
                                  alt={label}
                                  width={148}
                                  height={180}
                                  src={imageUrl}
                                  className='hidden h-auto w-full lg_block'
                                  unoptimized
                                />
                                <div className='mt-2.5 text-center text-[17px] leading-[1.4]'>{label}</div>
                              </HoverPrefetchLink>

                              <HoverPrefetchLink
                                href={href}
                                className={clsx(
                                  'mx-auto mt-2.5 hidden w-max items-center justify-center rounded-[3px] text-center leading-none lg_flex',
                                  'h-[34px] min-w-[130px] px-1 text-white',
                                  isSpring
                                    ? 'border-[#33662B] bg-[#33662B] lg_hover_text-[#33662B]'
                                    : 'border-[#B64A26] bg-[#B64A26] lg_hover_text-[#B64A26]',
                                  'border-1 border-solid lg_hover_bg-white',
                                )}
                              >
                                SHOP NOW
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>

                      <button
                        className={clsx(
                          'mx-auto mt-10 flex h-[34px] min-w-[130px] items-center justify-center rounded-[3px] border-1 border-solid bg-white px-2.5 text-center lg_hidden',
                          isSpring ? 'text-[#33662B]' : 'text-[#B64A26]',
                        )}
                        type='button'
                        onClick={() => {
                          setCurrentType('');
                        }}
                      >
                        BACK
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const DRESS_RECOMMENDATION_SESSION_KEY = `${process.env.NEXT_PUBLIC_STORE_VIEW_CODE}-dress-recommendation`;
const DRESS_RECOMMENDATION_LOCAL_KEY = `${process.env.NEXT_PUBLIC_STORE_VIEW_CODE}-dress-recommendation`;

const getSessionDressRecommendation = () => {
  const defaultInfo = {
    prevUrl: '',
    ignored: 0,
    prevPageIgnored: 0,
  };
  let dressRecommendationInfo = null;

  try {
    dressRecommendationInfo = sessionStorage.getItem(DRESS_RECOMMENDATION_SESSION_KEY);
  } catch (error) {
    console.error(error);
  }

  if (!dressRecommendationInfo) {
    return defaultInfo;
  }

  try {
    dressRecommendationInfo = JSON.parse(dressRecommendationInfo);
  } catch (error) {
    return defaultInfo;
  }

  if (typeof dressRecommendationInfo !== 'object' || dressRecommendationInfo === null) {
    return defaultInfo;
  }

  const { prevUrl, ignored, prevPageIgnored } = dressRecommendationInfo;

  return [
    typeof prevUrl === 'string',
    ignored === 0 || ignored === 1 || ignored === 2,
    prevPageIgnored === 0 || prevPageIgnored === 1,
  ].every(Boolean)
    ? dressRecommendationInfo
    : defaultInfo;
};

const setSessionDressRecommendation = data => {
  try {
    sessionStorage.setItem(DRESS_RECOMMENDATION_SESSION_KEY, JSON.stringify(data));
  } catch (error) {
    console.error(error);
  }
};

const removeSessionDressRecommendation = () => {
  try {
    sessionStorage.removeItem(DRESS_RECOMMENDATION_SESSION_KEY);
  } catch (error) {
    console.error(error);
  }
};

const getLocalDressRecommendation = () => {
  try {
    return localStorage.getItem(DRESS_RECOMMENDATION_LOCAL_KEY);
  } catch (error) {
    console.error(error);
    return null;
  }
};

const setLocalDressRecommendation = value => {
  try {
    localStorage.setItem(DRESS_RECOMMENDATION_LOCAL_KEY, value);
  } catch (error) {
    console.error(error);
  }
};

const springStyleConfigs = [
  {
    label: 'Chiffon Fabric',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/chiffon-fabric.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/chiffon-fabric-m.webp',
    href: '/bridesmaid-dresses/fabric/chiffon',
  },
  {
    label: 'Crystal Satin',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/crystal-satin.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/crystal-satin-m.webp',
    href: '/bridesmaid-dresses/fabric/crystal-satin',
  },
  {
    label: 'Strapless',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/strapless.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/strapless-m.webp',
    href: '/bridesmaid-dresses/straps-sleeves/strapless',
  },
  {
    label: 'Off the Shoulder',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/off-the-shoulder.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/off-the-shoulder-m.webp',
    href: '/bridesmaid-dresses/straps-sleeves/off-the-shoulder',
  },
  {
    label: 'Ankle Length',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/ankle-length.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/ankle-length-m.webp',
    href: '/bridesmaid-dresses/hemline/ankle-length',
  },
  {
    label: 'Ruching Design',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/ruching-design.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/ruching-design-m.webp',
    href: '/bridesmaid-dresses/embellishment/ruching',
  },
];

const springColorConfigs = [
  {
    label: 'Floral Color',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/floral-color.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/floral-color-m.webp',
    href: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/floral`,
  },
  {
    label: 'Sage Groon',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/sage-groon.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/sage-groon-m.webp',
    href: '/bridesmaid-dresses/color/sage-green',
  },
  {
    label: 'Dusty Blue',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/dusty-blue.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/dusty-blue-m.webp',
    href: '/bridesmaid-dresses/color/dusty-blue',
  },
  {
    label: 'Pearl Pink',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/pearl-pink.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/pearl-pink-m.webp',
    href: '/bridesmaid-dresses/color/pearl-pink',
  },
  {
    label: 'Ankle Length',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/lilac.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/lilac-m.webp',
    href: '/bridesmaid-dresses/color/lilac',
  },
  {
    label: 'Ruching Design',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/champagne.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-spring-dress-recommendation/champagne-m.webp',
    href: '/bridesmaid-dresses/color/champagne',
  },
];

const fallStyleConfigs = [
  {
    label: 'Satin Charmeuse',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/satin-charmeuse.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/satin-charmeuse-m.webp',
    href: '/bridesmaid-dresses/fabric/satin-charmeuse',
    field: 'satin_charmeuse',
  },
  {
    label: 'Velvet Fabric',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/velvet-fabric.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/velvet-fabric-m.webp',
    href: '/bridesmaid-dresses/fabric/velvet',
    field: 'velvet_fabric',
  },
  {
    label: 'Long Sleeves',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/long-sleeves.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/long-sleeves-m.webp',
    href: '/bridesmaid-dresses/straps-sleeves/long-sleeves',
    field: 'long_sleeves',
  },
  {
    label: 'Short Sleeves',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/short-sleeves.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/short-sleeves-m.webp',
    href: '/bridesmaid-dresses/straps-sleeves/short-sleeves',
    field: 'short_sleeves',
  },
  {
    label: 'Floor Length',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/floor-length.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/floor-length-m.webp',
    href: '/bridesmaid-dresses/hemline/floor-length',
    field: 'floor_length',
  },
  {
    label: 'Ruffle Design',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/ruffle-design.webp',
    mobileImageUrl:
      process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/ruffle-design-m.webp',
    href: '/bridesmaid-dresses/embellishment/ruffles',
    field: 'ruffle_design',
  },
];

const fallColorConfigs = [
  {
    label: websiteEnv.isUnitedKingdom ? 'Floral Colour' : 'Floral Color',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/floral-color.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/floral-color-m.webp',
    href: `/bridesmaid-dresses/${COLORS_FAMILY_KEY}/floral`,
    field: 'floral_color',
  },
  {
    label: 'Hunter Green',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/hunter-green.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/hunter-green-m.webp',
    href: '/bridesmaid-dresses/color/hunter-green',
    field: 'hunter_green',
  },
  {
    label: 'Navy',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/navy.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/navy-m.webp',
    href: '/bridesmaid-dresses/color/navy',
    field: 'navy',
  },
  {
    label: 'Burgundy',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/burgundy.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/burgundy-m.webp',
    href: '/bridesmaid-dresses/color/burgundy',
    field: 'burgundy',
  },
  {
    label: 'Burnt Orange',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/burnt-orange.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/burnt-orange-m.webp',
    href: '/bridesmaid-dresses/color/burnt-orange',
    field: 'burnt_orange',
  },
  {
    label: 'Champagne',
    imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/champagne.webp',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/styling-fall-dress-recommendation/champagne-m.webp',
    href: '/bridesmaid-dresses/color/champagne',
    field: 'champagne',
  },
];

export default DressStyleRecommendation;
